import {
  useState, useRef, useEffect, useMemo,
} from 'react';
import { MarkerF, InfoBoxF } from '@react-google-maps/api';
import { useAppTheme } from '../../../../theme/ThemeContext';
import PointIcon from './point-circle.svg';
import PointIconLight from './point-circle-light.svg';
import S from './PointMarkerStyles';
import { convertMsToKm } from '../../../../utils/unitsUtils';

type Props = {
  lat: number;
  lng: number;
  speed?: number;
  timestampSecEpoch?: number;
};

function PointMarker({
  lat,
  lng,
  speed,
  timestampSecEpoch,
}: Props): JSX.Element {
  const { themeMode } = useAppTheme();
  const [showInfoBox, setShowInfoBox] = useState(false);
  const infoBoxRef = useRef<HTMLDivElement>(null);
  const pointIconUrl = themeMode === 'light' ? PointIconLight : PointIcon;

  const handleClickOutside = (event: MouseEvent): void => {
    if (infoBoxRef.current && !infoBoxRef.current.contains(event.target as Node)) {
      setShowInfoBox(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const infoBoxOpts = {
    closeBoxURL: '',
    infoBoxClearance: new window.google.maps.Size(24, 24),
    pixelOffset: new window.google.maps.Size(-40, -10),
    alignBottom: true,
  };

  const gpsIcon = useMemo(() => ({
    url: pointIconUrl,
    size: new google.maps.Size(20, 20),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(10, 10),
  }), [pointIconUrl]);

  const localTime = timestampSecEpoch !== undefined
    ? new Date(timestampSecEpoch * 1000).toLocaleTimeString('en-GB', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    : undefined;

  return (
    <MarkerF
      key={themeMode}
      position={{ lat, lng }}
      icon={gpsIcon}
      zIndex={0}
      onMouseOver={() => setShowInfoBox(true)}
      onMouseOut={() => setShowInfoBox(false)}
    >
      {showInfoBox && (
        <InfoBoxF options={infoBoxOpts}>
          <S.InfoBox ref={infoBoxRef}>
            <div className="message">
              Speed: {speed !== undefined ? `${convertMsToKm(speed)} km/h` : 'n/a'}
            </div>
            <div className="message">
              Time: {localTime ?? 'n/a'}
            </div>
            <div className="message">
              Epoch: {timestampSecEpoch ?? 'n/a'}
            </div>
            <div className="message">
              Lat: {lat}
            </div>
            <div className="message">
              Lng: {lng}
            </div>
          </S.InfoBox>
        </InfoBoxF>
      )}
    </MarkerF>
  );
}

export default PointMarker;
